import { useEffect, useRef, useState, useCallback } from 'react';
import { XMarkIcon, CheckIcon, ArrowUturnLeftIcon, ArrowUturnRightIcon } from '@heroicons/react/24/outline';
import { useTranslation } from '../../hooks/useTranslation';

/**
 * 🎨 Modal de Pintura Completo
 * Modal fullscreen com todas as funcionalidades de pintura
 * 
 * Melhorias v2.0:
 * ✅ Preview do pincel no cursor
 * ✅ Atalhos de teclado (P, E, Ctrl+Z, Ctrl+Y, [, ])
 * ✅ Confirmação ao fechar com alterações
 * ✅ Loading state ao aplicar
 * ✅ Não reinicializa ao trocar ferramenta
 */
const PaintingModal = ({ isOpen, onClose, onApply, sourceImage }) => {
  const { t } = useTranslation();
  const canvasContainerRef = useRef(null);
  const croquisRef = useRef(null);
  const brushRef = useRef(null);
  const brushPreviewRef = useRef(null);
  const [isInitialized, setIsInitialized] = useState(false);
  
  // Estados de pintura - PINTURA + BORRACHA
  const [brushSize, setBrushSize] = useState(20);
  const [brushColor, setBrushColor] = useState('#000000');
  const [brushOpacity, setBrushOpacity] = useState(100);
  const [brushFlow, setBrushFlow] = useState(6);
  const [brushSpacing, setBrushSpacing] = useState(2);
  const [brushAngle, setBrushAngle] = useState(0);
  const [brushRotate, setBrushRotate] = useState(false);
  const [currentTool, setCurrentTool] = useState('pen');
  const [stabilizerLevel, setStabilizerLevel] = useState(10);
  const [stabilizerWeight, setStabilizerWeight] = useState(50);
  const [currentBrushImage, setCurrentBrushImage] = useState(null);
  const [selectedBrushIndex, setSelectedBrushIndex] = useState(0);
  const [strokeCount, setStrokeCount] = useState(0);
  const [isApplying, setIsApplying] = useState(false);
  const [brushPreviewPosition, setBrushPreviewPosition] = useState({ x: -1000, y: -1000 });
  const [blendMode, setBlendMode] = useState('source-over'); // Blend mode do pincel

  // Lista de pincéis para DESENHAR
  const drawingBrushes = [
    { name: 'Círculo Suave', type: 'circle', flow: 6, spacing: 2 },
    { name: 'Pixel', src: '/brush/pixel.webp', flow: 100, spacing: 20 },
    { name: 'Textura 25%', src: '/brush/25.webp', flow: 100, spacing: 10 },
    { name: 'Textura 50%', src: '/brush/50.webp', flow: 100, spacing: 10 },
    { name: 'Pincel 1', src: '/brush/b0.webp', flow: 30, spacing: 5 },
    { name: 'Pincel 2', src: '/brush/b1.webp', flow: 35, spacing: 7 },
    { name: 'Acrílico 1', src: '/brush/acrylic1.webp', flow: 50, spacing: 15 },
    { name: 'Acrílico 2', src: '/brush/acrylic2.webp', flow: 50, spacing: 20 },
    { name: 'Giz', src: '/brush/chalk1.webp', flow: 35, spacing: 7 },
    { name: 'Carvão', src: '/brush/charcoal1.webp', flow: 40, spacing: 8 },
  ];

  const presetColors = [
    '#000000', '#FFFFFF', '#FF0000', '#00FF00', '#0000FF',
    '#FFFF00', '#FF00FF', '#00FFFF', '#FFA500', '#800080'
  ];

  // Modos de mesclagem disponíveis (todos os modos do aplicativo)
  const blendModes = [
    { value: 'source-over', label: 'Normal' },
    { value: 'multiply', label: 'Multiplicar' },
    { value: 'screen', label: 'Tela' },
    { value: 'overlay', label: 'Sobreposição' },
    { value: 'soft-light', label: 'Luz Suave' },
    { value: 'hard-light', label: 'Luz Forte' },
    { value: 'color-dodge', label: 'Subexposição de Cor' },
    { value: 'color-burn', label: 'Superexposição de Cor' },
    { value: 'darken', label: 'Escurecer' },
    { value: 'lighten', label: 'Clarear' },
    { value: 'difference', label: 'Diferença' },
    { value: 'exclusion', label: 'Exclusão' },
    { value: 'hue', label: 'Matiz' },
    { value: 'saturation', label: 'Saturação' },
    { value: 'color', label: 'Cor' },
    { value: 'luminosity', label: 'Luminosidade' },
  ];

  // ✅ FIX 1: Inicializar Croquis APENAS UMA VEZ (sem currentTool nas deps)
  useEffect(() => {
    if (!isOpen || !sourceImage || !window.Croquis || !canvasContainerRef.current) {
      return;
    }

    // Validar imagem
    if (!sourceImage.width || !sourceImage.height) {
      console.error('❌ Imagem inválida');
      return;
    }

    // Limpar canvas anterior se existir
    if (croquisRef.current) {
      const element = croquisRef.current.getDOMElement();
      if (element?.parentNode) {
        element.parentNode.removeChild(element);
      }
      croquisRef.current = null;
    }

    // Criar Croquis
    const croquis = new window.Croquis();
    croquisRef.current = croquis;

    // Criar pincel
    const brush = new window.Croquis.Brush();
    brush.setSize(brushSize);
    brush.setColor(brushColor);
    brush.setSpacing(brushSpacing / 100);
    brush.setFlow(brushFlow / 100);
    brush.setAngle(brushAngle);
    brush.setRotateToDirection(brushRotate);
    brushRef.current = brush;

    croquis.setTool(brush);
    croquis.setToolStabilizeLevel(stabilizerLevel);
    croquis.setToolStabilizeWeight(stabilizerWeight / 100);
    croquis.setPaintingOpacity(brushOpacity / 100);
    croquis.setPaintingKnockout(currentTool === 'eraser');

    // Configurar canvas
    croquis.lockHistory();
    croquis.setCanvasSize(sourceImage.width, sourceImage.height);
    
    // Adicionar APENAS UMA camada com a imagem
    croquis.addLayer();
    const workingCanvas = croquis.getLayers()[0].children[0];
    const ctx = workingCanvas.getContext('2d');
    
    // Desenhar a imagem na camada de trabalho
    ctx.clearRect(0, 0, sourceImage.width, sourceImage.height);
    ctx.drawImage(sourceImage, 0, 0);
    
    // Selecionar esta camada para trabalhar
    croquis.selectLayer(0);
    croquis.setUndoLimit(100);
    croquis.unlockHistory();

    // Adicionar ao DOM
    const croquisElement = croquis.getDOMElement();
    
    // ✅ Esconder cursor padrão APENAS dentro do canvas
    const canvas = croquisElement.querySelector('canvas');
    if (canvas) {
      canvas.style.cursor = 'none'; // Esconde cursor nativo para mostrar nosso preview
    }
    
    canvasContainerRef.current.appendChild(croquisElement);

    // Event handlers
    let isDrawing = false;

    const getRelativePosition = (e) => {
      const canvas = croquisElement.querySelector('canvas');
      if (!canvas) return null;
      
      const rect = canvas.getBoundingClientRect();
      const rx = e.clientX - rect.left;
      const ry = e.clientY - rect.top;
      
      const scaleX = sourceImage.width / rect.width;
      const scaleY = sourceImage.height / rect.height;
      
      return {
        x: rx * scaleX,
        y: ry * scaleY,
        clientX: e.clientX,
        clientY: e.clientY
      };
    };

    const handlePointerDown = (e) => {
      e.preventDefault();
      e.stopPropagation();
      
      const pos = getRelativePosition(e);
      if (!pos) return;
      
      isDrawing = true;
      
      try {
        croquis.down(pos.x, pos.y, e.pressure || 1);
      } catch (error) {
        console.error('Erro ao iniciar traço:', error);
      }
    };

    const handlePointerMove = (e) => {
      if (!isDrawing) return;
      
      e.preventDefault();
      e.stopPropagation();
      
      const pos = getRelativePosition(e);
      if (!pos) return;
      
      // ✅ Atualizar preview TAMBÉM durante o desenho
      setBrushPreviewPosition({ x: pos.clientX, y: pos.clientY });
      
      try {
        croquis.move(pos.x, pos.y, e.pressure || 1);
      } catch (error) {
        console.error('Erro ao mover:', error);
      }
    };

    const handlePointerUp = (e) => {
      if (!isDrawing) return;
      
      e.preventDefault();
      e.stopPropagation();
      
      const pos = getRelativePosition(e);
      isDrawing = false;
      
      if (pos) {
        try {
          croquis.up(pos.x, pos.y, e.pressure || 1);
          setStrokeCount(prev => prev + 1);
        } catch (error) {
          console.error('Erro ao finalizar traço:', error);
        }
      }
    };

    // ✅ Handler separado APENAS para preview do cursor (dentro do canvas)
    const handleCanvasMouseMove = (e) => {
      const pos = getRelativePosition(e);
      if (pos) {
        setBrushPreviewPosition({ x: pos.clientX, y: pos.clientY });
      }
    };

    const handleCanvasMouseEnter = () => {
      // Mostrar preview ao entrar no canvas
    };

    const handleCanvasMouseLeave = () => {
      // Esconder preview ao sair do canvas
      setBrushPreviewPosition({ x: -1000, y: -1000 });
    };

    // Event listeners para desenho
    croquisElement.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('pointermove', handlePointerMove);
    document.addEventListener('pointerup', handlePointerUp);
    
    // Event listeners APENAS para preview (no canvas)
    croquisElement.addEventListener('mousemove', handleCanvasMouseMove);
    croquisElement.addEventListener('mouseenter', handleCanvasMouseEnter);
    croquisElement.addEventListener('mouseleave', handleCanvasMouseLeave);

    // ✅ Aplicar blend mode inicial
    if (currentTool === 'pen') {
      croquis.setPaintingBlendMode(blendMode);
    }

    setIsInitialized(true);

    // Cleanup
    return () => {
      // Remover listeners de desenho
      croquisElement.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('pointermove', handlePointerMove);
      document.removeEventListener('pointerup', handlePointerUp);
      
      // Remover listeners de preview
      croquisElement.removeEventListener('mousemove', handleCanvasMouseMove);
      croquisElement.removeEventListener('mouseenter', handleCanvasMouseEnter);
      croquisElement.removeEventListener('mouseleave', handleCanvasMouseLeave);
      
      // Esconder preview
      setBrushPreviewPosition({ x: -1000, y: -1000 });
    };
  }, [isOpen, sourceImage]); // ✅ Inicializa apenas uma vez

  // ✅ FIX 2: Alternar ferramenta SEM reinicializar
  useEffect(() => {
    if (!croquisRef.current || !isInitialized) return;
    
    croquisRef.current.setPaintingKnockout(currentTool === 'eraser');
  }, [currentTool, isInitialized]);

  // Atualizar propriedades do pincel
  useEffect(() => {
    if (!brushRef.current || !croquisRef.current || !isInitialized) return;
    
    brushRef.current.setSize(brushSize);
    brushRef.current.setColor(brushColor);
    brushRef.current.setSpacing(brushSpacing / 100);
    brushRef.current.setFlow(brushFlow / 100);
    brushRef.current.setAngle(brushAngle);
    brushRef.current.setRotateToDirection(brushRotate);
    croquisRef.current.setPaintingOpacity(brushOpacity / 100);
    croquisRef.current.setToolStabilizeLevel(stabilizerLevel);
    croquisRef.current.setToolStabilizeWeight(stabilizerWeight / 100);
  }, [brushSize, brushColor, brushOpacity, brushFlow, brushSpacing, brushAngle, brushRotate, stabilizerLevel, stabilizerWeight, isInitialized]);

  // ✅ Atualizar blend mode quando mudar
  useEffect(() => {
    if (!croquisRef.current || !isInitialized || currentTool === 'eraser') return;
    
    croquisRef.current.setPaintingBlendMode(blendMode);
  }, [blendMode, isInitialized, currentTool]);

  // Atualizar imagem do pincel
  useEffect(() => {
    if (!brushRef.current || !currentBrushImage) return;
    
    const img = new Image();
    img.onload = () => {
      brushRef.current.setImage(img);
    };
    img.onerror = () => {
      console.error('Erro ao carregar pincel:', currentBrushImage);
    };
    img.src = currentBrushImage;
  }, [currentBrushImage]);

  // ✅ FIX 3: Atalhos de teclado
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      // Ignorar se estiver digitando em um input
      if (e.target.tagName === 'INPUT') return;

      // P = Caneta
      if (e.key.toLowerCase() === 'p') {
        setCurrentTool('pen');
        e.preventDefault();
      }
      
      // E = Borracha
      if (e.key.toLowerCase() === 'e') {
        setCurrentTool('eraser');
        e.preventDefault();
      }

      // Ctrl/Cmd + Z = Undo
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z' && !e.shiftKey) {
        handleUndo();
        e.preventDefault();
      }

      // Ctrl/Cmd + Y ou Ctrl/Cmd + Shift + Z = Redo
      if ((e.ctrlKey || e.metaKey) && (e.key.toLowerCase() === 'y' || (e.key.toLowerCase() === 'z' && e.shiftKey))) {
        handleRedo();
        e.preventDefault();
      }

      // [ = Diminuir pincel
      if (e.key === '[') {
        setBrushSize(prev => Math.max(1, prev - 5));
        e.preventDefault();
      }

      // ] = Aumentar pincel
      if (e.key === ']') {
        setBrushSize(prev => Math.min(200, prev + 5));
        e.preventDefault();
      }

      // Esc = Fechar (com confirmação)
      if (e.key === 'Escape') {
        handleClose();
        e.preventDefault();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, strokeCount]);

  // ✅ FIX 4: Aplicar com loading e tratamento de erro
  const handleApply = useCallback(async () => {
    if (!croquisRef.current || isApplying) return;
    
    setIsApplying(true);
    
    try {
      const flattenedCanvas = croquisRef.current.createFlattenThumbnail();
      
      return new Promise((resolve, reject) => {
        const img = new Image();
        
        img.onload = () => {
          onApply(img);
          setIsInitialized(false);
          setStrokeCount(0);
          setIsApplying(false);
          resolve();
        };
        
        img.onerror = () => {
          setIsApplying(false);
          reject(new Error('Erro ao criar imagem'));
        };
        
        img.src = flattenedCanvas.toDataURL('image/png');
      });
    } catch (error) {
      console.error('❌ Erro ao aplicar pintura:', error);
      alert('Erro ao aplicar pintura. Tente novamente.');
      setIsApplying(false);
    }
  }, [isApplying, onApply]);

  // ✅ FIX 5: Fechar com confirmação se houver alterações
  const handleClose = useCallback(() => {
    if (strokeCount > 0) {
      const confirmed = window.confirm(
        'Você tem alterações não salvas. Deseja realmente sair sem aplicar?'
      );
      if (!confirmed) return;
    }
    
    setStrokeCount(0);
    setIsInitialized(false);
    setBrushPreviewPosition({ x: -1000, y: -1000 });
    onClose();
  }, [strokeCount, onClose]);

  const handleUndo = useCallback(() => {
    if (!croquisRef.current) return;
    
    try {
      croquisRef.current.undo();
      setStrokeCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      // Silenciosamente ignorar se não há nada para desfazer
    }
  }, []);

  const handleRedo = useCallback(() => {
    if (!croquisRef.current) return;
    
    try {
      croquisRef.current.redo();
      setStrokeCount(prev => prev + 1);
    } catch (error) {
      // Silenciosamente ignorar se não há nada para refazer
    }
  }, []);

  const handleClear = useCallback(() => {
    if (!croquisRef.current) return;
    
    const confirmed = window.confirm('Limpar toda a camada? Esta ação não pode ser desfeita.');
    if (!confirmed) return;
    
    croquisRef.current.clearLayer();
    setStrokeCount(0);
  }, []);

  const handleSelectBrush = useCallback((brush, index) => {
    setSelectedBrushIndex(index);
    if (brush.src) {
      setCurrentBrushImage(brush.src);
    } else {
      setCurrentBrushImage(null);
    }
    setBrushFlow(brush.flow);
    setBrushSpacing(brush.spacing);
    setBrushRotate(brush.rotate || false);
  }, []);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[99999] bg-gray-900">
      {/* ✅ FIX 6: Loading Overlay */}
      {isApplying && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[100000]">
          <div className="bg-gray-800 rounded-lg p-8 flex flex-col items-center space-y-4">
            <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-green-500"></div>
            <p className="text-white text-lg font-medium">Aplicando pintura...</p>
          </div>
        </div>
      )}

      {/* ✅ FIX 7: Preview do Pincel */}
      <div
        ref={brushPreviewRef}
        className="fixed pointer-events-none z-[100001] transition-opacity"
        style={{
          left: brushPreviewPosition.x,
          top: brushPreviewPosition.y,
          transform: 'translate(-50%, -50%)',
          opacity: brushPreviewPosition.x > 0 ? 1 : 0,
        }}
      >
        <div
          className="rounded-full border-2 transition-all"
          style={{
            width: brushSize,
            height: brushSize,
            borderColor: currentTool === 'pen' ? brushColor : '#ef4444',
            backgroundColor: currentTool === 'pen' ? `${brushColor}20` : '#ef444420',
          }}
        >
          {/* Crosshair no centro */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-px h-3 bg-white opacity-50"></div>
            <div className="w-3 h-px bg-white opacity-50 absolute"></div>
          </div>
        </div>
      </div>

      {/* Header */}
      <div className="h-16 bg-gray-800 border-b border-gray-700 flex items-center justify-between px-6">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-3">
            <div className={`p-2 rounded ${currentTool === 'pen' ? 'bg-green-600' : 'bg-red-600'}`}>
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {currentTool === 'pen' ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                )}
              </svg>
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">
                {currentTool === 'pen' ? t('painting.modePen') : t('painting.modeEraser')}
              </h2>
              <p className="text-xs text-gray-400">
                {currentTool === 'pen' ? 'Desenhe sobre a imagem' : 'Apague partes criando transparência'}
              </p>
            </div>
          </div>
          
          {/* Ferramentas principais */}
          <div className="flex items-center space-x-2 ml-8">
            {/* Alternar Caneta/Borracha */}
            <button
              onClick={() => setCurrentTool('pen')}
              className={`px-3 py-2 rounded transition-all flex items-center space-x-2 ${
                currentTool === 'pen' 
                  ? 'bg-green-600 text-white shadow-lg' 
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
              title={`${t('painting.pen')} (P)`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
              <span className="text-sm font-medium">{t('painting.pen')}</span>
            </button>
            
            <button
              onClick={() => setCurrentTool('eraser')}
              className={`px-3 py-2 rounded transition-all flex items-center space-x-2 ${
                currentTool === 'eraser' 
                  ? 'bg-red-600 text-white shadow-lg' 
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
              title={`${t('painting.eraser')} (E)`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              <span className="text-sm font-medium">{t('painting.eraser')}</span>
            </button>

            <div className="w-px h-8 bg-gray-600 mx-2"></div>

            <button
              onClick={handleUndo}
              disabled={strokeCount === 0}
              className={`p-2 rounded transition-colors ${
                strokeCount > 0
                  ? 'bg-gray-700 text-white hover:bg-gray-600 cursor-pointer' 
                  : 'bg-gray-800 text-gray-600 cursor-not-allowed opacity-50'
              }`}
              title="Desfazer (Ctrl+Z)"
            >
              <ArrowUturnLeftIcon className="w-5 h-5" />
            </button>
            
            <button
              onClick={handleRedo}
              className="p-2 bg-gray-700 text-white hover:bg-gray-600 rounded transition-colors"
              title="Refazer (Ctrl+Y)"
            >
              <ArrowUturnRightIcon className="w-5 h-5" />
            </button>
            
            <button
              onClick={handleClear}
              className="p-2 bg-red-600 text-white hover:bg-red-700 rounded transition-colors"
              title="Limpar Camada"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>
        </div>

        {/* Botões de ação */}
        <div className="flex items-center space-x-3">
          {/* Info de atalhos */}
          <div className="mr-4 text-xs text-gray-400 space-y-1">
            <div dangerouslySetInnerHTML={{ __html: `💡 ${t('painting.shortcuts').replace(/P=/g, '<strong>P</strong>=').replace(/E=/g, '<strong>E</strong>=').replace(/\[/g, '<strong>[</strong>').replace(/\]/g, '<strong>]</strong>')}` }} />
            <div><strong>Ctrl+Z</strong>=Desfazer <strong>Ctrl+Y</strong>=Refazer <strong>Esc</strong>=Fechar</div>
          </div>
          
          <button
            onClick={handleClose}
            disabled={isApplying}
            className="px-5 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-md font-medium transition-colors flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            title="Fechar (Esc)"
          >
            <XMarkIcon className="w-5 h-5" />
            <span>Cancelar</span>
          </button>
          <button
            onClick={handleApply}
            disabled={isApplying || strokeCount === 0}
            className="px-5 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md font-medium transition-colors flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            title={strokeCount === 0 ? "Sem alterações para aplicar" : "Aplicar pintura"}
          >
            <CheckIcon className="w-5 h-5" />
            <span>Aplicar</span>
          </button>
        </div>
      </div>

      {/* Conteúdo Principal */}
      <div className="flex h-[calc(100vh-4rem)]">
        {/* Sidebar com controles - SEM ABAS */}
        <div className="w-80 bg-gray-800 border-r border-gray-700 flex flex-col">
          
          {/* SEÇÃO 1: AJUSTES */}
          <div className="flex-1 overflow-y-auto border-b border-gray-700">
            <div className="p-4 space-y-6">
              {/* Título da seção */}
              <h3 className="text-lg font-bold text-white border-b border-gray-700 pb-2 mb-4">
                {currentTool === 'pen' ? t('painting.brushSettings') : t('painting.eraserSettings')}
              </h3>
              
              {/* Info sobre ferramenta ativa */}
              <div className={`border rounded-lg p-3 mb-4 ${
                currentTool === 'pen' 
                  ? 'bg-green-900 bg-opacity-30 border-green-700' 
                  : 'bg-red-900 bg-opacity-30 border-red-700'
              }`}>
                <p className={`text-xs ${currentTool === 'pen' ? 'text-green-300' : 'text-red-300'}`}>
                  {currentTool === 'pen' ? (
                    <span dangerouslySetInnerHTML={{ __html: t('painting.penTip') }} />
                  ) : (
                    <span dangerouslySetInnerHTML={{ __html: t('painting.eraserTip') }} />
                  )}
                </p>
              </div>

              {/* Tamanho com Preview */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Tamanho: <span className={`font-bold ${currentTool === 'pen' ? 'text-green-400' : 'text-red-400'}`}>{brushSize}px</span>
                </label>
                
                {/* Preview visual do tamanho */}
                <div className="flex items-center justify-center mb-3 p-4 bg-gray-900 rounded-lg border border-gray-700">
                  <div
                    className="rounded-full border-2 transition-all"
                    style={{
                      width: Math.min(brushSize, 80),
                      height: Math.min(brushSize, 80),
                      borderColor: currentTool === 'pen' ? brushColor : '#ef4444',
                      backgroundColor: currentTool === 'pen' ? `${brushColor}30` : '#ef444430',
                    }}
                  />
                </div>
                
                <input
                  type="range"
                  min="1"
                  max="200"
                  step="1"
                  value={brushSize}
                  onChange={(e) => setBrushSize(parseFloat(e.target.value))}
                  className={`w-full h-3 bg-gray-700 rounded-lg appearance-none cursor-pointer ${
                    currentTool === 'pen' ? 'accent-green-600' : 'accent-red-600'
                  }`}
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>Pequeno (1px)</span>
                  <span>Grande (200px)</span>
                </div>
              </div>

              {/* Opacidade/Intensidade */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  {currentTool === 'pen' ? 'Opacidade' : 'Intensidade'}: <span className={`font-bold ${currentTool === 'pen' ? 'text-green-400' : 'text-red-400'}`}>{brushOpacity}%</span>
                </label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={brushOpacity}
                  onChange={(e) => setBrushOpacity(parseInt(e.target.value))}
                  className={`w-full h-3 bg-gray-700 rounded-lg appearance-none cursor-pointer ${
                    currentTool === 'pen' ? 'accent-green-600' : 'accent-red-600'
                  }`}
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>Leve (0%)</span>
                  <span>Total (100%)</span>
                </div>
              </div>

              {/* Seletor de Cor - APENAS no modo caneta */}
              {currentTool === 'pen' && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-3">Cor do Pincel</label>
                  <div className="flex items-center space-x-3 mb-3">
                    <input
                      type="color"
                      value={brushColor}
                      onChange={(e) => setBrushColor(e.target.value)}
                      className="w-10 h-10 border-2 border-gray-600 rounded-full cursor-pointer bg-gray-700"
                    />
                    <input
                      type="text"
                      value={brushColor}
                      onChange={(e) => setBrushColor(e.target.value)}
                      className="flex-1 px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm"
                    />
                  </div>

                  {/* Cores predefinidas - minimalista com círculos pequenos em grid 8 colunas */}
                  <div className="grid grid-cols-8 gap-2 mb-4">
                    {presetColors.map(color => (
                      <button
                        key={color}
                        onClick={() => setBrushColor(color)}
                        className={`w-6 h-6 rounded-full border transition-all ${
                          brushColor.toUpperCase() === color.toUpperCase()
                            ? 'border-white border-2 scale-125 ring-2 ring-white ring-opacity-40 shadow-lg'
                            : 'border-gray-500 hover:scale-125 hover:border-white'
                        }`}
                        style={{ backgroundColor: color }}
                        title={color}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* ✅ Modo de Mesclagem - APENAS no modo caneta */}
              {currentTool === 'pen' && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Modo de Mesclagem</label>
                  <select
                    value={blendMode}
                    onChange={(e) => setBlendMode(e.target.value)}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    {blendModes.map(mode => (
                      <option key={mode.value} value={mode.value}>
                        {mode.label}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    {blendMode === 'source-over' && '✨ Desenho padrão sem mesclagem'}
                    {blendMode === 'multiply' && '🌑 Escurece - perfeito para sombras'}
                    {blendMode === 'screen' && '💡 Clareia - perfeito para luzes'}
                    {blendMode === 'overlay' && '✨ Aumenta contraste mantendo extremos'}
                    {blendMode === 'soft-light' && '🌤️ Clareia/escurece suavemente'}
                    {blendMode === 'hard-light' && '⚡ Clareia/escurece intensamente'}
                    {blendMode === 'color-dodge' && '☀️ Clareia intenso - brilhos fortes'}
                    {blendMode === 'color-burn' && '🔥 Escurece intenso - sombras profundas'}
                    {blendMode === 'darken' && '⬇️ Mantém apenas pixels mais escuros'}
                    {blendMode === 'lighten' && '⬆️ Mantém apenas pixels mais claros'}
                    {blendMode === 'difference' && '🎭 Inverte cores - efeito artístico'}
                    {blendMode === 'exclusion' && '🎨 Similar a diferença mais suave'}
                    {blendMode === 'hue' && '🌈 Aplica apenas a matiz da cor'}
                    {blendMode === 'saturation' && '🎨 Aplica apenas a saturação'}
                    {blendMode === 'color' && '🎨 Aplica matiz e saturação'}
                    {blendMode === 'luminosity' && '💡 Aplica apenas a luminosidade'}
                  </p>
                </div>
              )}

              {/* Fluxo */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Fluxo: <span className="text-green-400">{brushFlow}%</span>
                </label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={brushFlow}
                  onChange={(e) => setBrushFlow(parseInt(e.target.value))}
                  className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-green-600"
                />
              </div>

              {/* Espaçamento */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Espaçamento: <span className="text-green-400">{brushSpacing}%</span>
                </label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={brushSpacing}
                  onChange={(e) => setBrushSpacing(parseInt(e.target.value))}
                  className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-green-600"
                />
              </div>

              {/* Ângulo */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Ângulo: <span className="text-green-400">{brushAngle}°</span>
                </label>
                <input
                  type="range"
                  min="0"
                  max="360"
                  value={brushAngle}
                  onChange={(e) => setBrushAngle(parseInt(e.target.value))}
                  className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-green-600"
                />
              </div>

              {/* Rotação com direção */}
              <div>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={brushRotate}
                    onChange={(e) => setBrushRotate(e.target.checked)}
                    className="rounded border-gray-600 text-green-600 focus:ring-green-500 bg-gray-700"
                  />
                  <span className="text-sm font-medium text-gray-300">Rotacionar com direção</span>
                </label>
              </div>

              {/* Estabilização */}
              <div className="border-t border-gray-700 pt-4">
                <h4 className="text-sm font-medium text-gray-300 mb-3">Estabilização</h4>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Nível: <span className="text-green-400">{stabilizerLevel}</span>
                  </label>
                  <input
                    type="range"
                    min="3"
                    max="20"
                    value={stabilizerLevel}
                    onChange={(e) => setStabilizerLevel(parseInt(e.target.value))}
                    className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-green-600"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Peso: <span className="text-green-400">{stabilizerWeight}</span>
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="80"
                    value={stabilizerWeight}
                    onChange={(e) => setStabilizerWeight(parseInt(e.target.value))}
                    className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-green-600"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* SEÇÃO 2: PINCÉIS/TIPOS */}
          <div className="h-96 overflow-y-auto bg-gray-850">
            <div className="p-4">
              {/* Título da seção */}
              <h3 className="text-lg font-bold text-white border-b border-gray-700 pb-2 mb-3">
                {currentTool === 'pen' ? t('painting.brushTypes') : t('painting.eraserTypes')}
              </h3>
              
              {/* Grid de pincéis - 4 colunas */}
              <div className="grid grid-cols-4 gap-1.5">
                {drawingBrushes.map((brush, index) => (
                  <button
                    key={index}
                    onClick={() => handleSelectBrush(brush, index)}
                    className={`p-2 border-2 rounded-lg transition-all hover:scale-105 ${
                      selectedBrushIndex === index
                        ? `${currentTool === 'pen' ? 'border-green-500 shadow-green-500/30' : 'border-red-500 shadow-red-500/30'} bg-gray-700 shadow-lg`
                        : 'border-gray-600 hover:border-gray-500 bg-gray-750'
                    }`}
                  >
                    {brush.src ? (
                      <img src={brush.src} alt={brush.name} className="w-full h-10 object-contain" />
                    ) : (
                      <div className="w-full h-10 flex items-center justify-center">
                        <div className="w-6 h-6 rounded-full bg-white"></div>
                      </div>
                    )}
                    <span className="block text-[10px] mt-1.5 text-gray-300 text-center truncate">{brush.name}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Área do Canvas */}
        <div 
          className="flex-1 flex items-center justify-center p-8"
          style={{
            // ✅ Padrão quadriculado em TODA a área externa
            background: `
              linear-gradient(45deg, #FFFFFF 25%, transparent 25%),
              linear-gradient(-45deg, #FFFFFF 25%, transparent 25%),
              linear-gradient(45deg, transparent 75%, #FFFFFF 75%),
              linear-gradient(-45deg, transparent 75%, #FFFFFF 75%)
            `,
            backgroundColor: '#CCCCCC',
            backgroundSize: '20px 20px',
            backgroundPosition: '0 0, 0 10px, 10px -10px, -10px 0px'
          }}
        >
          <div
            ref={canvasContainerRef}
            className="shadow-2xl"
            style={{
              touchAction: 'none',
              userSelect: 'none'
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default PaintingModal;
